import React, { useState, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import apiService from '../../services/apiService';
import uploadQueue from '../../services/uploadQueue';
import { compressImage } from '../../utils/imageUtils';
import FotoPreviewModal from '../common/FotoPreviewModal';
import GaleriaPickerModal from '../galeria/GaleriaPickerModal';

/**
 * Componente de upload de evidencia optimizado para terreno.
 * Muestra modal con preview, descripción y micrófono (mismo patrón que CapturaFoto inline).
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} props.actividadId - ID de la actividad
 * @param {string} props.tipo - 'antes' o 'despues'
 * @param {Function} props.onUploaded - Callback tras upload exitoso
 * @param {boolean} [props.disabled=false] - Deshabilitar upload
 * @returns {JSX.Element}
 */
function EvidenciaUpload({ actividadId, tipo, onUploaded, disabled = false }) {
  const { showToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [attachingFromGaleria, setAttachingFromGaleria] = useState(false);

  const resetState = () => {
    if (preview?.src) URL.revokeObjectURL(preview.src);
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (selectedFile.type.startsWith('image/')) {
      const compressed = await compressImage(selectedFile);
      setFile(compressed);
      setPreview({ type: 'image', src: URL.createObjectURL(compressed) });
    } else if (selectedFile.type.startsWith('video/')) {
      setFile(selectedFile);
      setPreview({ type: 'video', src: URL.createObjectURL(selectedFile) });
    }
  };

  const handleUpload = async (descripcion) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('tipo', tipo);
      if (descripcion) formData.append('descripcion', descripcion);

      const response = await apiService.uploadEvidencia(actividadId, formData);
      if (response.success) {
        showToast('Evidencia subida', 'success');
        resetState();
        if (onUploaded) onUploaded();
      } else {
        showToast(response.message || 'Error al subir', 'error');
      }
    } catch (error) {
      if (error.message === 'NETWORK_ERROR') {
        try {
          await uploadQueue.add({
            file,
            uploadFn: async (f, meta) => {
              const fd = new FormData();
              fd.append('archivo', f);
              fd.append('tipo', meta.tipo);
              if (meta.descripcion) fd.append('descripcion', meta.descripcion);
              await apiService.uploadEvidencia(meta.actividadId, fd);
            },
            metadata: { actividadId, tipo, descripcion: descripcion || null }
          });
        } catch (queueErr) {
          showToast('Error al encolar upload offline', 'error');
          return;
        }
        showToast('Sin conexion. Upload en cola.', 'warning');
        resetState();
      } else {
        showToast(error.message || 'Error al subir evidencia', 'error');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSelectFromGaleria = async (foto) => {
    setPickerOpen(false);
    setAttachingFromGaleria(true);
    try {
      const response = await apiService.adjuntarFotoComoEvidencia(actividadId, {
        foto_galeria_id: foto.id,
        tipo
      });
      if (response.success) {
        showToast('Foto adjuntada como evidencia', 'success');
        if (onUploaded) onUploaded();
      } else {
        showToast(response.message || 'Error al adjuntar foto', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Error al adjuntar foto', 'error');
    } finally {
      setAttachingFromGaleria(false);
    }
  };

  return (
    <div className="evidencia-upload">
      <div className="evidencia-upload-btns">
        <label className="evidencia-upload-btn" title={disabled ? 'No disponible' : `Agregar foto ${tipo === 'despues' ? 'después' : tipo}`}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/quicktime"
            onChange={handleFileSelect}
            disabled={disabled}
            className="evidencia-file-input"
          />
          <i className="fas fa-plus"></i>
          <span>Subir</span>
        </label>
        <button
          className="evidencia-upload-btn"
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={disabled || attachingFromGaleria}
          title="Elegir foto de mi galería"
        >
          {attachingFromGaleria
            ? <i className="fas fa-spinner fa-spin" />
            : <i className="fas fa-images" />
          }
          <span>Galería</span>
        </button>
      </div>

      <FotoPreviewModal
        isOpen={!!(file && preview)}
        previewSrc={preview?.src}
        previewType={preview?.type}
        title="Agregar evidencia"
        uploading={uploading}
        onSave={handleUpload}
        onCancel={resetState}
      />

      <GaleriaPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelectFromGaleria}
      />
    </div>
  );
}

export default EvidenciaUpload;
